import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import useToast from "../../components/common/useToast";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler } from "../../utils/HelperFunctions";
import "../../assets/css/datatable.css";
import DeleteConfirmation from "../../components/modal-popup/delete-popup";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { Dropdown } from "primereact/dropdown";
import { BuildingOffice2Icon, EyeIcon } from "@heroicons/react/24/outline";

const BusinessPage: React.FC = () => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

    const [businessData, setBusinessData] = useState<any[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);
    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalRecords, settotalRecords] = useState(0);
    const [totalPages, settotalPages] = useState<any>(0);
    const { showToast }: any = useToast();
    const [businessToDelete, setBusinessToDelete] = useState<any | null>(null);
    const [name, setName] = useState<string>("");
    const [sortField, setSortField] = useState<string>("createddate");
    const [sortOrder, setSortOrder] = useState<any>(-1);
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);

    const startIndex = currentPage * itemsPerPage;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    async function getBusinessData() {
        try {
            setDataLoad(true);

            const params: any = {
                page: currentPage + 1,
                limit: itemsPerPage,
                sortField: sortField || "createddate",
                sortOrder: sortOrder === 1 ? "asc" : "desc",
            };
            if (name) params.search = name;

            const response = await apiFunctions.get("/business/admin/list", { params });

            if (response?.data?.success) {
                setBusinessData(response.data.data);
                settotalRecords(response.data.totalRecords);
                settotalPages(response.data.totalPages);
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response?.data?.message || "Failed to load businesses",
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setDataLoad(false);
            setLoad(false);
        }
    }

    const confirmDelete = async () => {
        if (businessToDelete) {
            try {
                const response = await apiFunctions.delete(`/business/admin/${businessToDelete._id}`);
                if (response?.data?.success) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Business deleted successfully", life: 3000 });
                } else {
                    showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Delete failed", life: 3000 });
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setDeleteConfirmationVisible(false);
                setBusinessToDelete(null);
                getBusinessData();
            }
        }
    };

    const handleDelete = (rowData: any) => {
        setBusinessToDelete(rowData);
        setDeleteConfirmationVisible(true);
    };

    const hideDeleteConfirmation = () => {
        setDeleteConfirmationVisible(false);
        setBusinessToDelete(null);
    };

    const rowSkeletonTemplate = () => <Skeleton height="1.25rem" className="rounded" />;

    const logoSkeletonTemplate = () => <Skeleton shape="rectangle" width="40px" height="40px" className="rounded" />;

    const actionBodyTemplate = (rowData: any, mobile: boolean = false) => {
        return (
            <div className={`flex items-center gap-1 ${mobile ? "flex-wrap" : "flex-nowrap"}`}>
                <Button label="View" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => navigate(`/admin/business/view/${rowData._id}`)} />
                <Button label="Edit" outlined size="small" style={{ color: "#1a7a4a", borderColor: "#1a7a4a" }} onClick={() => navigate(`/admin/business/edit/${rowData._id}`)} />
                <Button label="Viewers" outlined size="small" style={{ color: "#7c3aed", borderColor: "#7c3aed" }} onClick={() => navigate(`/admin/business/viewers/${rowData._id}`)} />
                <Button
                    label="Advertise"
                    outlined
                    size="small"
                    style={{ color: "#c026d3", borderColor: "#c026d3" }}
                    onClick={() => navigate("/admin/advertisement/add", {
                        state: {
                            prefill: {
                                company_name: rowData.name || "",
                                mobile_number: rowData.mobileNo || "",
                                whatsapp_number: rowData.whatsappNo || "",
                                email: rowData.email || "",
                                website_link: rowData.link || "",
                                userid: rowData.userid || "",
                                ownerName: rowData.ownerName || "",
                                imageUrl: rowData.logo || "",
                                short_description: rowData.description && rowData.description.length <= 30 ? rowData.description : "",
                                long_description: rowData.description ? rowData.description : "",
                            }
                        }
                    })}
                />
                <Button label="Delete" outlined size="small" style={{ color: "#dc2626", borderColor: "#dc2626" }} onClick={() => handleDelete(rowData)} />
            </div>
        );
    };

    const onSort = (e: any) => {
        setSortOrder(e.sortOrder);
        setSortField(e.sortField);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const getsearchdata = (e: any) => {
        if (e.target.value.length >= 2) {
            setName(e.target.value);
            setCurrentPage(0);
        } else {
            setName("");
            setLoad(false);
        }
    };

    useEffect(() => {
        if (!load) {
            getBusinessData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, name, sortField, sortOrder]);

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getBusinessData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const header = (
        <div className="grid grid-cols-12 gap-4 items-center table-header">
            <div className="col-span-12 sm:col-span-6">
                <h2 className="card-title m-0 text-lg sm:text-xl">Business</h2>
            </div>
            <div className="col-span-12 sm:col-span-6 flex gap-2 justify-end flex-wrap">
                <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-gray-600">Search:</span>
                    <PrimeInputText
                        type="text"
                        className="search-input w-full sm:w-auto"
                        onChange={(e) => getsearchdata(e)}
                    />
                </div>
                <Button
                    label="Add Business"
                    severity="success"
                    onClick={() => navigate("/admin/business/add")}
                />
            </div>
        </div>
    );

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    return (
        <>
            <div className="dashboard-margin">
                {isMobile ? (
                    <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                        <div className="p-3 pb-2">{header}</div>
                        <div className="flex flex-col gap-2 px-3 pb-3">
                            {dataLoad ? (
                                Array.from({ length: 5 }).map((_, i) => (
                                    <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <div className="flex items-start gap-2 w-full">
                                            <Skeleton shape="rectangle" width="40px" height="40px" className="rounded flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <Skeleton height="1rem" width="60%" className="rounded mb-1" />
                                                <Skeleton height="0.75rem" width="80%" className="rounded" />
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1 mt-2">
                                            <Skeleton height="0.75rem" width="50%" className="rounded" />
                                            <Skeleton height="0.75rem" width="70%" className="rounded" />
                                            <Skeleton height="0.75rem" width="40%" className="rounded" />
                                        </div>
                                    </div>
                                ))
                            ) : businessData.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "64px 0" }}>
                                    <BuildingOffice2Icon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                    <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No business found</p>
                                </div>
                            ) : (
                                businessData.map((rowData) => (
                                    <div key={rowData._id} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <div className="flex items-start gap-2 w-full">
                                            {rowData.logo
                                                ? <img src={rowData.logo} alt={rowData.name} style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "6px", flexShrink: 0 }} />
                                                : <BuildingOffice2Icon style={{ width: "32px", height: "32px", color: "#d1d5db", flexShrink: 0 }} />
                                            }
                                            <div className="flex-1 min-w-0">
                                                <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>{rowData.name}</p>
                                                <p className="m-0 text-xs break-words" style={{ color: "#9ca3af" }}>{rowData.ownerName || "-"} · {rowData.businessType || "-"}</p>
                                            </div>
                                            <span className={`px-2 py-1 rounded text-white text-xs flex-shrink-0 ${rowData.isactive ? "bg-green-500" : "bg-red-500"}`}>
                                                {rowData.isactive ? "Active" : "Inactive"}
                                            </span>
                                        </div>
                                        <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                            <div
                                                className="flex items-center gap-1 cursor-pointer w-max"
                                                style={{ color: "#264164" }}
                                                onClick={() => navigate(`/admin/business/viewers/${rowData._id}`)}
                                            >
                                                <EyeIcon style={{ width: "14px", height: "14px" }} />
                                                {rowData.viewCount ?? 0} views
                                            </div>
                                            <div><span className="font-medium">Contact:</span> {rowData.mobileNo || rowData.whatsappNo || "-"}</div>
                                            <div className="break-words"><span className="font-medium">Email:</span> {rowData.email || "-"}</div>
                                            <div><span className="font-medium">Created:</span> {rowData.createddate ? new Date(rowData.createddate).toLocaleDateString("en-GB") : "-"}</div>
                                        </div>
                                        <div className="flex flex-wrap justify-end gap-1 mt-2">
                                            {actionBodyTemplate(rowData, true)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ) : (
                    <DataTable
                        lazy
                        scrollable
                        scrollHeight="calc(100vh - 250px)"
                        style={{ minHeight: "calc(100vh - 175px)", background: "#fff" }}
                        header={header}
                        value={dataLoad ? Array.from({ length: itemsPerPage }, (_, i) => ({ _id: `skeleton-${i}` })) : businessData}
                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={onSort}
                        resizableColumns
                        removableSort
                        rows={itemsPerPage}
                        emptyMessage={
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <BuildingOffice2Icon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No business found</p>
                            </div>
                        }
                    >
                        <Column
                            header="Logo"
                            style={{ minWidth: "70px" }}
                            body={dataLoad ? logoSkeletonTemplate : (rowData) =>
                                rowData.logo
                                    ? <img src={rowData.logo} alt={rowData.name} style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "6px" }} />
                                    : <BuildingOffice2Icon style={{ width: "32px", height: "32px", color: "#d1d5db" }} />
                            }
                        />
                        <Column field="name" header="Name" sortable={!dataLoad} style={{ minWidth: "180px" }} body={dataLoad ? rowSkeletonTemplate : undefined} />
                        <Column field="ownerName" header="Owner" sortable={!dataLoad} style={{ minWidth: "150px" }} body={dataLoad ? rowSkeletonTemplate : (rowData) => rowData.ownerName || "-"} />
                        <Column field="businessType" header="Business Type" sortable={!dataLoad} style={{ minWidth: "140px" }} body={dataLoad ? rowSkeletonTemplate : (rowData) => rowData.businessType || "-"} />
                        <Column
                            header="Views"
                            style={{ minWidth: "100px" }}
                            body={dataLoad ? rowSkeletonTemplate : (rowData) => (
                                <span
                                    className="flex items-center gap-1 cursor-pointer"
                                    style={{ color: "#264164" }}
                                    onClick={() => navigate(`/admin/business/viewers/${rowData._id}`)}
                                >
                                    <EyeIcon style={{ width: "16px", height: "16px" }} />
                                    {rowData.viewCount ?? 0}
                                </span>
                            )}
                        />
                        <Column
                            header="Contact"
                            style={{ minWidth: "140px" }}
                            body={dataLoad ? rowSkeletonTemplate : (rowData) => rowData.mobileNo || rowData.whatsappNo || "-"}
                        />
                        <Column field="email" header="Email" style={{ minWidth: "180px" }} body={dataLoad ? rowSkeletonTemplate : (rowData) => rowData.email || "-"} />
                        <Column
                            field="isactive"
                            header="Status"
                            sortable={!dataLoad}
                            style={{ minWidth: "100px" }}
                            body={dataLoad ? rowSkeletonTemplate : (rowData) => (
                                <span className={`px-2 py-1 rounded text-white text-sm ${rowData.isactive ? "bg-green-500" : "bg-red-500"}`}>
                                    {rowData.isactive ? "Active" : "Inactive"}
                                </span>
                            )}
                        />
                        <Column
                            field="createddate"
                            header="Created Date"
                            sortable={!dataLoad}
                            style={{ minWidth: "130px" }}
                            body={dataLoad ? rowSkeletonTemplate : (rowData) => rowData.createddate ? new Date(rowData.createddate).toLocaleDateString("en-GB") : "-"}
                        />
                        <Column header="Action" className="action-column" frozen alignFrozen="right" style={{ minWidth: "480px" }} body={dataLoad ? rowSkeletonTemplate : (rowData) => actionBodyTemplate(rowData)} />
                    </DataTable>
                )}
                {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    <Paginator
                        template={paginatoreTemplate}
                        first={startIndex}
                        rows={itemsPerPage}
                        totalRecords={totalRecords}
                    />
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage === 0}
                        >
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>

                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                Page {currentPage + 1} of {totalPages || 1}
                            </span>
                        </Button>

                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                        >
                            Next
                        </Button>
                    </div>
                </div>}
            </div>
            <DeleteConfirmation
                visible={deleteConfirmationVisible}
                onHide={hideDeleteConfirmation}
                onDelete={confirmDelete}
                header="Delete Business"
                message={`Are you sure you want to delete "${businessToDelete?.name}"? This action cannot be undone.`}
            />
        </>
    );
};

export default BusinessPage;
